import React from "react";
import {
  AbsoluteFill,
  Easing,
  interpolate,
  spring,
  useCurrentFrame,
} from "remotion";

const P = {
  ink: "#11100E",
  paper: "#EAE6DD",
  white: "#F8F5ED",
  lime: "#CFFF38",
  orange: "#FF6846",
  blue: "#4F63FF",
  pink: "#FF68B5",
};

const clamp = {
  extrapolateLeft: "clamp" as const,
  extrapolateRight: "clamp" as const,
};

const ease = Easing.bezier(0.16, 1, 0.3, 1);
const cameraEase = Easing.bezier(0.65, 0, 0.35, 1);

const p = (
  frame: number,
  from: number,
  to: number,
  easing: (value: number) => number = ease,
) =>
  interpolate(frame, [from, to], [0, 1], {
    ...clamp,
    easing,
  });

const windowOpacity = (
  frame: number,
  enter: [number, number],
  exit: [number, number],
) =>
  interpolate(frame, [...enter, ...exit], [0, 1, 1, 0], {
    ...clamp,
    easing: cameraEase,
  });

type CameraState = {
  x: number;
  y: number;
  scale: number;
  rotation: number;
};

const cameraState = (frame: number): CameraState => {
  const times = [0, 80, 165, 225, 315, 375, 465, 525, 600];

  return {
    x: interpolate(frame, times, [0, 0, -2200, -2200, -2200, -2200, 0, 0, 0], {
      ...clamp,
      easing: cameraEase,
    }),
    y: interpolate(
      frame,
      times,
      [0, 0, 100, 100, -1450, -1450, -1450, -1450, 0],
      { ...clamp, easing: cameraEase },
    ),
    scale: interpolate(
      frame,
      times,
      [1, 1.05, 0.92, 1.04, 0.9, 1.06, 0.94, 1.08, 0.82],
      { ...clamp, easing: cameraEase },
    ),
    rotation: interpolate(
      frame,
      times,
      [0, -1.5, 2.2, 0, -2.5, 0.7, 2, 0, -1],
      { ...clamp, easing: cameraEase },
    ),
  };
};

const TableGrid: React.FC = () => (
  <div className="tj-grid" aria-hidden="true" />
);

const CornerData: React.FC<{frame: number}> = ({frame}) => {
  return (
    <div className="tj-corner-data">
      <span>DESIGN TABLE / CAMERA 01</span>
      <span>{String(frame).padStart(4, "0")} F</span>
      <span>1920 × 1080 / 30 FPS</span>
    </div>
  );
};

const MorphCursor: React.FC<{frame: number}> = ({frame}) => {
  const travelA = p(frame, 82, 158, cameraEase);
  const travelB = p(frame, 232, 308, cameraEase);
  const travelC = p(frame, 382, 458, cameraEase);
  const travelD = p(frame, 532, 592, cameraEase);
  const travel = Math.max(travelA, travelB, travelC, travelD);
  const size = interpolate(travel, [0, 0.45, 1], [24, 80, 24], clamp);
  const radius = interpolate(travel, [0, 0.5, 1], [50, 10, 50], clamp);
  const rotate = interpolate(travel, [0, 1], [0, 180], clamp);
  const colors = [P.lime, P.orange, P.blue, P.pink, P.lime];
  const section = Math.min(4, Math.floor(frame / 150));

  return (
    <div
      className="tj-morph-cursor"
      style={{
        width: size,
        height: size,
        borderRadius: `${radius}%`,
        backgroundColor: colors[section],
        transform: `translate(-50%, -50%) rotate(${rotate}deg)`,
      }}
    />
  );
};

const TypeGroup: React.FC<{frame: number}> = ({frame}) => {
  const intro = spring({
    frame,
    fps: 30,
    config: { damping: 22, stiffness: 90, mass: 1 },
  });
  const split = p(frame, 24, 68);
  const opacity = windowOpacity(frame, [0, 20], [92, 150]);

  return (
    <section
      className="tj-group tj-type-group"
      style={{ opacity }}
      aria-label="Typography study"
    >
      <div className="tj-group-index">01 / TYPOGRAPHY</div>
      <div className="tj-type-stage">
        <div
          className="tj-type-line tj-type-line-one"
          style={{
            transform: `translate3d(${(1 - intro) * -160}px, ${split * -34}px, 0)`,
          }}
        >
          MOVEMENT
        </div>
        <div
          className="tj-type-line tj-type-line-two"
          style={{
            transform: `translate3d(${(1 - intro) * 160}px, ${split * 34}px, 0)`,
          }}
        >
          WITH PURPOSE
        </div>
        <div
          className="tj-type-mask"
          style={{
            transform: `scaleX(${p(frame, 10, 58)})`,
          }}
        />
      </div>
      <div className="tj-note tj-note-a">
        <span>A</span>
        Form follows rhythm.
      </div>
      <div
        className="tj-orbit"
        style={{
          transform: `rotate(${interpolate(frame, [0, 145], [-40, 75], clamp)}deg)`,
        }}
      >
        <div />
      </div>
    </section>
  );
};

const MaterialCard: React.FC<{
  index: number;
  color: string;
  label: string;
  frame: number;
}> = ({ index, color, label, frame }) => {
  const local = frame - index * 8;
  const enter = spring({
    frame: local,
    fps: 30,
    config: { damping: 20, stiffness: 110, mass: 0.9 },
  });
  const tilt = interpolate(enter, [0, 1], [index % 2 ? 10 : -10, 0]);

  return (
    <div
      className="tj-material-card"
      style={{
        backgroundColor: color,
        opacity: enter,
        transform: `translateY(${(1 - enter) * 150}px) rotate(${tilt}deg)`,
      }}
    >
      <span>0{index + 1}</span>
      <strong>{label}</strong>
      <div className="tj-card-rule" />
    </div>
  );
};

const MaterialGroup: React.FC<{frame: number}> = ({frame}) => {
  const opacity = windowOpacity(frame, [0, 28], [105, 145]);
  const lens = p(frame, 48, 112, cameraEase);

  return (
    <section
      className="tj-group tj-material-group"
      style={{opacity, translate: "132.9px 67.3px"}}
      aria-label="Material and color study"
    >
      <div className="tj-group-index">02 / MATERIAL</div>
      <h2 className="tj-material-title">
        Color becomes
        <br />
        <em>physical.</em>
      </h2>
      <div className="tj-material-cards">
        <MaterialCard index={0} color={P.lime} label="ENERGY" frame={frame} />
        <MaterialCard index={1} color={P.orange} label="HEAT" frame={frame} />
        <MaterialCard index={2} color={P.blue} label="DEPTH" frame={frame} />
      </div>
      <div
        className="tj-lens"
        style={{
          transform: `translate3d(${interpolate(lens, [0, 1], [300, -60])}px, ${interpolate(lens, [0, 1], [-90, 80])}px, 0) scale(${0.55 + lens * 0.45})`,
        }}
      />
    </section>
  );
};

const ShapeIcon: React.FC<{
  variant: "circle" | "cross" | "wave";
  color: string;
  delay: number;
  frame: number;
}> = ({ variant, color, delay, frame }) => {
  const enter = spring({
    frame: frame - delay,
    fps: 30,
    config: { damping: 18, stiffness: 105, mass: 0.8 },
  });
  const rotate = interpolate(
    frame,
    [0, 135],
    [0, variant === "cross" ? 135 : -90],
    clamp,
  );

  return (
    <div
      className="tj-icon-tile"
      style={{
        opacity: enter,
        transform: `translateY(${(1 - enter) * 110}px) scale(${0.72 + enter * 0.28})`,
      }}
    >
      <svg viewBox="0 0 260 260" aria-hidden="true">
        {variant === "circle" && (
          <g transform={`rotate(${rotate} 130 130)`}>
            <circle
              cx="130"
              cy="130"
              r="86"
              fill="none"
              stroke={color}
              strokeWidth="25"
            />
            <circle cx="130" cy="44" r="23" fill={P.pink} />
          </g>
        )}
        {variant === "cross" && (
          <g transform={`rotate(${rotate} 130 130)`}>
            <rect x="104" y="32" width="52" height="196" rx="26" fill={color} />
            <rect x="32" y="104" width="196" height="52" rx="26" fill={color} />
          </g>
        )}
        {variant === "wave" && (
          <path
            d="M 30 142 C 65 45, 102 45, 130 142 C 158 239, 195 239, 230 142"
            fill="none"
            stroke={color}
            strokeWidth="30"
            strokeLinecap="round"
            strokeDasharray="480"
            strokeDashoffset={480 * (1 - enter)}
          />
        )}
      </svg>
      <span>{variant}</span>
    </div>
  );
};

const IconGroup: React.FC<{frame: number}> = ({frame}) => {
  const opacity = windowOpacity(frame, [0, 26], [105, 145]);
  const title = p(frame, 4, 38);

  return (
    <section
      className="tj-group tj-icon-group"
      style={{ opacity }}
      aria-label="Icon motion study"
    >
      <div className="tj-group-index">03 / SYMBOLS</div>
      <h2
        className="tj-icon-title"
        style={{
          opacity: title,
          transform: `translateX(${(1 - title) * -120}px)`,
        }}
      >
        One language.
        <br />
        <em>Many gestures.</em>
      </h2>
      <div className="tj-icon-row">
        <ShapeIcon variant="circle" color={P.lime} delay={10} frame={frame} />
        <ShapeIcon variant="cross" color={P.orange} delay={20} frame={frame} />
        <ShapeIcon variant="wave" color={P.blue} delay={30} frame={frame} />
      </div>
      <div className="tj-connection-line">
        <div style={{ transform: `scaleX(${p(frame, 30, 92)})` }} />
      </div>
    </section>
  );
};

const FinaleGroup: React.FC<{frame: number}> = ({frame}) => {
  const opacity = windowOpacity(frame, [0, 28], [108, 145]);
  const reveal = p(frame, 5, 44);
  const expand = p(frame, 64, 126, cameraEase);
  const chars = "CONNECTED".split("");

  return (
    <section
      className="tj-group tj-finale-group"
      style={{ opacity }}
      aria-label="Connected motion finale"
    >
      <div className="tj-group-index">04 / CONTINUITY</div>
      <div className="tj-final-words">
        {chars.map((char, index) => {
          const letter = spring({
            frame: frame - index * 3,
            fps: 30,
            config: { damping: 20, stiffness: 120 },
          });

          return (
            <span
              key={`${char}-${index}`}
              style={{
                opacity: letter,
                color:
                  index < Math.floor(expand * chars.length) ? P.lime : P.white,
                transform: `translateY(${(1 - letter) * 100}px) rotate(${(1 - letter) * 8}deg)`,
              }}
            >
              {char}
            </span>
          );
        })}
      </div>
      <p
        className="tj-final-copy"
        style={{
          opacity: reveal,
          transform: `translateY(${(1 - reveal) * 30}px)`,
        }}
      >
        One continuous camera. Four ideas. No hard cuts.
      </p>
      <div
        className="tj-final-circle"
        style={{
          transform: `translate(-50%, -50%) scale(${0.2 + expand * 1.8})`,
          opacity: 1 - expand * 0.65,
        }}
      />
    </section>
  );
};

const World: React.FC<{frame: number}> = ({frame}) => {
  const camera = cameraState(frame);

  return (
    <div
      className="tj-world"
      style={{
        transform: `translate3d(${camera.x}px, ${camera.y}px, 0) scale(${camera.scale}) rotate(${camera.rotation}deg)`,
      }}
    >
      <TableGrid />
      <div className="tj-world-label">
        A CONTINUOUS DESIGN SURFACE / 5200 × 3400
      </div>
      <TypeGroup frame={frame} />
      <MaterialGroup frame={frame - 150} />
      <IconGroup frame={frame - 300} />
      <FinaleGroup frame={frame - 450} />
    </div>
  );
};

const ParallaxDust: React.FC<{frame: number}> = ({frame}) => {
  const camera = cameraState(frame);

  return (
    <div
      className="tj-parallax"
      style={{
        transform: `translate3d(${camera.x * 0.12}px, ${camera.y * 0.12}px, 0) rotate(${camera.rotation * 0.2}deg)`,
      }}
    >
      {Array.from({ length: 18 }).map((_, index) => (
        <i
          key={index}
          style={{
            left: `${(index * 137) % 100}%`,
            top: `${(index * 83) % 100}%`,
            width: index % 3 === 0 ? 8 : 3,
            height: index % 3 === 0 ? 8 : 3,
            background: index % 4 === 0 ? P.lime : P.white,
          }}
        />
      ))}
    </div>
  );
};

export const TableJourney: React.FC = () => {
  const frame = useCurrentFrame() * 1.5;
  const intro = p(frame, 0, 24);
  const end = interpolate(frame, [570, 600], [1, 0], {
    ...clamp,
    easing: Easing.in(Easing.cubic),
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: P.ink,
        opacity: intro * end,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          translate: "74px -248.9px",
        }}
      >
        <World frame={frame} />
      </div>
      <ParallaxDust frame={frame} />
      <MorphCursor frame={frame} />
      <CornerData frame={frame} />
      <div className="tj-vignette" />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
